import { initializeApp } from 'firebase/app'
import {
  getMessaging, getToken, isSupported as isMessagingSupported,
} from 'firebase/messaging'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { FCM } from '@capacitor-community/fcm'
import { handleNotificationTap } from '@/utils/notificationNavigation'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FCM_API_KEY,
  authDomain: import.meta.env.VITE_FCM_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FCM_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FCM_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FCM_APP_ID,
}

/**
 * Initialize FCM for native Capacitor Android app
 * This uses @capacitor/push-notifications and @capacitor-community/fcm plugins
 */
async function initializeNativeFcm () {
  try {
    // Check current permission status first
    const currentStatus = await PushNotifications.checkPermissions()
    
    // If already denied, don't try to request (it won't show dialog anyway)
    if (currentStatus.receive === 'denied') {
      return null
    }

    // Request permission to use push notifications (only if not already granted)
    const permissionStatus = await PushNotifications.requestPermissions()

    if (permissionStatus.receive !== 'granted') {
      return null
    }

    // Create a promise that will resolve when we get the token from the registration event
    let registrationErrorListener = null
    let registrationListener = null
    
    const tokenPromise = new Promise((resolve, reject) => {
      // Add timeout to prevent infinite waiting
      const timeout = setTimeout(() => {
        // Clean up listeners
        if (registrationErrorListener) {
          registrationErrorListener.remove()
        }
        if (registrationListener) {
          registrationListener.remove()
        }
        reject(new Error('Registration timeout'))
      }, 15000)

      // Set up listeners for push notifications
      registrationListener = PushNotifications.addListener('registration', async (token) => {
        clearTimeout(timeout)

        // Clean up listeners
        if (registrationErrorListener) {
          registrationErrorListener.remove()
        }
        registrationListener.remove()

        // Subscribe to 'general' topic
        try {
          await FCM.subscribeTo({ topic: 'general' })
        } catch (e) {
          console.error('Error subscribing to FCM topic:', e)
        }

        // On iOS, token.value is the APNs token. We need the FCM token for the backend.
        if (Capacitor.getPlatform() === 'ios') {
          try {
            const fcmToken = await FCM.getToken()
            resolve(fcmToken.token)
          } catch (e) {
            console.error('Error getting FCM token on iOS:', e)
            resolve(token.value) // Fallback to APNs token
          }
        } else {
          // On Android, token.value is already the FCM token
          resolve(token.value)
        }
      })

      // Also listen for registration errors
      registrationErrorListener = PushNotifications.addListener('registrationError', (error) => {
        console.error('Push notification registration error:', error)
        clearTimeout(timeout)
        
        // Clean up listeners
        registrationErrorListener.remove()
        if (registrationListener) {
          registrationListener.remove()
        }
        
        // Check if it's the aps-environment error
        const errorMessage = error?.message || error?.toString() || JSON.stringify(error) || ''
        if (errorMessage.includes('aps-environment') || errorMessage.includes('entitlement')) {
          reject(new Error('iOS push notification entitlements not configured'))
        } else {
          reject(error)
        }
      })
    })

    // Handle received notifications (these listeners persist)
    PushNotifications.addListener('pushNotificationReceived', () => {
      // Notification received - can be used for foreground handling if needed
    })

    // Take the user to the screen the notification is about. The payload's
    // `url` and `log_id` are set by the backend sender.
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      handleNotificationTap(action?.notification?.data || {})
    })

    // Register with FCM - this will trigger the registration event
    try {
      await PushNotifications.register()
    } catch (registerError) {
      console.error('Failed to register for push notifications:', registerError)
      // Clean up listeners
      if (registrationErrorListener) {
        registrationErrorListener.remove()
      }
      if (registrationListener) {
        registrationListener.remove()
      }
      throw registerError
    }

    // Wait for the token from the registration event
    const token = await tokenPromise
    return token
  } catch (err) {
    console.error('Native FCM initialization error:', err)
    return null
  }
}

/**
 * Initialize FCM for web PWA using service worker and VAPID key
 */
async function initializeWebFcm ({ requestPermission = true } = {}) {
  const pushSupported = await isMessagingSupported()
  const secureOrigin = window.isSecureContext || ['localhost', '127.0.0.1'].includes(location.hostname)

  if (!pushSupported || !secureOrigin || !('serviceWorker' in navigator) || !('Notification' in window)) {
    return null
  }

  if (!requestPermission && Notification.permission !== 'granted') {
    return null
  }

  const app = initializeApp(firebaseConfig)
  const messaging = getMessaging(app)

  try {
    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js')

    const permission = requestPermission
      ? await Notification.requestPermission()
      : Notification.permission
    
    if (permission !== 'granted') {
      throw new Error('Permission not granted')
    }

    const token = await getToken(messaging, {
      serviceWorkerRegistration: swReg, vapidKey: import.meta.env.VITE_FCM_VAPID_KEY,
    })

    return token
  } catch (err) {
    console.error('Web FCM token error:', err)
    return null
  }
}

/**
 * Request FCM token based on platform context
 * Uses Capacitor.isNativePlatform() to differentiate between native and web contexts
 */
export async function requestFcmToken (options = {}) {
  // Check if running on a native platform (Android/iOS) or web
  if (Capacitor.isNativePlatform()) {
    return await initializeNativeFcm()
  } else {
    return await initializeWebFcm(options)
  }
}
